/**
 * DestinationParameterRow Component
 *
 * The 2-field pattern for destination parameter mapping:
 * 1. Source Type (dropdown): URL | Server | Variable | Custom
 * 2. Source Item (dropdown from selected source type)
 *
 * The output name is automatically derived from the selected parameter's key
 * and displayed as read-only information.
 *
 * Used in: RedirectParametersSection, StripeMetadataSection, WebhooksSection
 *
 * @module webplacements/components/editors/destinations
 */

import React, { useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import TuneIcon from '@mui/icons-material/Tune';
import type {
  ParameterDefinition,
  ParameterSource,
} from '@modules/webplacements/types/webplacements.types';

/**
 * Mapping configuration for a single destination parameter
 */
export interface DestinationParameterMapping {
  /** Source type: url | server | variable | custom */
  sourceType: ParameterSource;
  /** Key of the parameter from that source (also used as output name) */
  sourceKey: string;
}

export interface DestinationParameterRowProps {
  /** All defined parameters from all sources */
  availableParameters: ParameterDefinition[];
  /** Current mapping configuration */
  value: DestinationParameterMapping;
  /** Called when mapping changes */
  onChange: (mapping: DestinationParameterMapping) => void;
  /** Called to remove this row */
  onRemove: () => void;
  /** Whether the row is disabled */
  disabled?: boolean;
  /** Row index for data-testid generation */
  index?: number;
}

/**
 * Source type configuration for display
 * Colors are applied dynamically using theme palette in the component
 */
const SOURCE_CONFIG: Record<
  ParameterSource,
  { label: string; icon: React.ReactNode; themeColor: 'primary' | 'secondary' | 'success' | 'warning' | 'info' }
> = {
  url: {
    label: 'URL',
    icon: <LinkIcon fontSize="small" />,
    themeColor: 'primary',
  },
  server: {
    label: 'Server',
    icon: <StorageIcon fontSize="small" />,
    themeColor: 'secondary',
  },
  variable: {
    label: 'Variable',
    icon: <TuneIcon fontSize="small" />,
    themeColor: 'info',
  },
  custom: {
    label: 'Custom',
    icon: <CodeIcon fontSize="small" />,
    themeColor: 'warning',
  },
};

/**
 * DestinationParameterRow Component
 *
 * Provides a 2-field interface for mapping a source parameter to a destination.
 * The output key is automatically derived from the selected source parameter.
 */
export const DestinationParameterRow: React.FC<DestinationParameterRowProps> = ({
  availableParameters,
  value,
  onChange,
  onRemove,
  disabled = false,
  index,
}) => {
  const theme = useTheme();

  // Filter parameters by selected source type
  const filteredParameters = useMemo(
    () => availableParameters.filter((p) => p.source === value.sourceType),
    [availableParameters, value.sourceType]
  );

  // Get available source types that have parameters
  const availableSourceTypes = useMemo(() => {
    const types = new Set(availableParameters.map((p) => p.source));
    return Array.from(types) as ParameterSource[];
  }, [availableParameters]);

  const handleSourceTypeChange = useCallback(
    (newType: ParameterSource) => {
      // Find first parameter of new type
      const firstParam = availableParameters.find((p) => p.source === newType);
      onChange({
        sourceType: newType,
        sourceKey: firstParam?.key || '',
      });
    },
    [availableParameters, onChange]
  );

  const handleSourceKeyChange = useCallback(
    (newKey: string) => {
      onChange({
        ...value,
        sourceKey: newKey,
      });
    },
    [value, onChange]
  );

  const selectedParam = filteredParameters.find((p) => p.key === value.sourceKey);

  // Get theme color for current source type
  const getSourceColor = (type: ParameterSource) => {
    const colorKey = SOURCE_CONFIG[type].themeColor;
    return theme.palette[colorKey].main;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2,
        mb: 1,
        bgcolor: theme.palette.action.hover,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Source Type Dropdown */}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Source</InputLabel>
        <Select
          data-testid={index !== undefined ? `wp-dest-param-source-type-${index}` : undefined}
          value={value.sourceType}
          onChange={(e) => handleSourceTypeChange(e.target.value as ParameterSource)}
          label="Source"
          disabled={disabled}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getSourceColor(selected) }}>
              {SOURCE_CONFIG[selected].icon}
              <span>{SOURCE_CONFIG[selected].label}</span>
            </Box>
          )}
        >
          {availableSourceTypes.map((type) => (
            <MenuItem key={type} value={type}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: getSourceColor(type) }}>
                {SOURCE_CONFIG[type].icon}
                <span>{SOURCE_CONFIG[type].label}</span>
              </Box>
            </MenuItem>
          ))}
          {availableSourceTypes.length === 0 && (
            <MenuItem disabled value="">
              No parameters defined
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Source Item Dropdown */}
      <FormControl size="small" sx={{ flex: 1 }}>
        <InputLabel>Parameter</InputLabel>
        <Select
          data-testid={index !== undefined ? `wp-dest-param-source-key-${index}` : undefined}
          value={value.sourceKey}
          onChange={(e) => handleSourceKeyChange(e.target.value)}
          label="Parameter"
          disabled={disabled || filteredParameters.length === 0}
        >
          {filteredParameters.map((param) => (
            <MenuItem key={param.key} value={param.key}>
              <Box>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{param.key}</Box>
                <Box sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{param.label}</Box>
              </Box>
            </MenuItem>
          ))}
          {filteredParameters.length === 0 && (
            <MenuItem disabled value="">
              No {SOURCE_CONFIG[value.sourceType].label} parameters
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Output key chip */}
      {selectedParam && (
        <Chip
          label={`{{${selectedParam.key}}}`}
          size="small"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            bgcolor: alpha(getSourceColor(value.sourceType), 0.15),
            color: getSourceColor(value.sourceType),
            alignSelf: 'center',
          }}
        />
      )}

      {/* Delete Button */}
      <IconButton
        data-testid={index !== undefined ? `wp-dest-param-delete-${index}` : undefined}
        size="small"
        onClick={onRemove}
        disabled={disabled}
        color="error"
        sx={{ alignSelf: 'center', ml: 'auto' }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default DestinationParameterRow;
